<template>
  <v-expand-transition>
    <v-card flat v-if="selectedPerson" class="v-card__container">
      <v-form ref="form" lazy-validation>
        <v-row v-if="editing">
          <v-col cols="12" md="6">
            <v-text-field
              label="Förnamn"
              outlined
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.firstName"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="Efternamn"
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.lastName"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mb-6" v-if="editing">
            <v-card id="personid-card" outlined class="input-group d-flex justify-center align-center">
              <legend for="paersonid-card" class="v-label v-label--active theme--light">Personnummer</legend>
              <v-radio-group v-model="selectedPerson.personalNumberType" class="ma-6" :hide-details="true">
                <v-radio label="Svenskt" :value="1"></v-radio>
                <v-radio label="Utländskt" :value="2"></v-radio>
              </v-radio-group>
              <v-text-field
                v-if="selectedPerson.personalNumberType !== 2"
                class="pt-6 pr-6"
                outlined
                :append-icon="showPersonalNumber ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPersonalNumber = !showPersonalNumber"
                :hide-details="!editing"
                :disabled="!editing"
                :readonly="!editing"
                v-model="maskedPersonalnumber"
                placeholder="YYYYMMDDXXXX"
                :rules="
                  showPersonalNumber
                    ? [
                        rules.required,
                        rules.integer,
                        rules.hasLength(12, selectedPerson.personalNumber),
                        rules.swePnr,
                        ...userFilters
                      ]
                    : []
                "
              />

              <v-text-field
                v-if="selectedPerson.personalNumberType === 2"
                :type="showPersonalNumber ? 'text' : 'password'"
                :append-icon="showPersonalNumber ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPersonalNumber = !showPersonalNumber"
                class="pt-6 pr-6"
                outlined
                :hide-details="!editing"
                :disabled="!editing"
                :readonly="!editing"
                v-model="selectedPerson.personalNumber"
                :rules="[rules.required]"
                placeholder="YYMMDDZZZXQ"
              />
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="">
            <v-text-field
              outlined
              label="Adress (ange även ev. lägenhetsnummer)"
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.address1"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="Mobiltelefon"
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.mobilephone"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="Adress (rad2)"
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.address2"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="E-postadress"
              outlined
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.email"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="Postnummer"
              :hide-details="!editing"
              :disabled="!editing"
              :readonly="!editing"
              v-model="selectedPerson.zip"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              data-cy="select-country"
              :disabled="!editing"
              :hide-details="!editing"
              label="Land"
              outlined
              :items="countries"
              item-value="key"
              item-text="value"
              v-model="selectedPerson.country"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Ort"
              outlined
              :disabled="!editing"
              :readonly="!editing"
              :hide-details="!editing"
              v-model="selectedPerson.city"
            />
          </v-col>
          <v-col class="text-right"></v-col>
        </v-row>
        <v-card-actions class="justify-space-between no-margins mt-4">
          <v-btn @click="editing = !editing" class="icon" color="secondary" :disabled="!canUpdatePerson">
            <v-icon v-if="!editing">$sl-view-on</v-icon>
            <v-icon v-else>$sl-cancel</v-icon>
            {{ editing ? 'Avbryt' : 'Visa/Ändra' }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="icon" v-if="editing" @click="saveContactInfo()" :disabled="!canUpdatePerson"> <v-icon>$sl-check</v-icon>Spara </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-expand-transition>
</template>

<script>
import API from '@/services/api.js'
import {mask} from 'vue-the-mask'
import {mapGetters, mapActions} from 'vuex'
import {RulesMixin, CanIDo} from '@/components/mixins'
import pick from 'lodash/pick'
import orderBy from 'lodash/orderBy'

// @group Person
// PersonContactInfo component
export default {
  name: 'PersonContactInfo',

  props: {
    // New person?
    isNew: {
      type: Boolean,
      // false
      default: false
    }
  },

  mixins: [RulesMixin, CanIDo],

  directives: {
    mask
  },

  data: () => ({
    mask: '############',
    editing: false,
    savedCopy: {},
    fields: [
      'firstName',
      'lastName',
      'personalNumber',
      'address1',
      'mobilephone',
      'address2',
      'email',
      'zip',
      'city',
      'country',
      'personalNumberType'
    ],
    showPersonalNumber: false,
    countries: []
  }),

  computed: {
    ...mapGetters('person', ['selectedPerson']),
    ...mapGetters('currentuser', ['currentUserRole']),
    userFilters() {
      const userFilters = []
      if (this.currentUserRole === 'region') userFilters.push(this.rules.notMinor)
      return userFilters
    },
    maskedPersonalnumber: {
      get: function () {
        if (this.showPersonalNumber) {
          return this.selectedPerson.personalNumber
        }
        if (this.selectedPerson && this.selectedPerson.personalNumber) {
          return this.selectedPerson.personalNumber.substring(0, 8) + '-****'
        } else {
          this.selectedPerson.personalNumber = ''
          this.showPersonalNumber = true
          return ''
        }
      },
      set: function (newValue) {
        this.selectedPerson.personalNumber = newValue
        return newValue
      }
    }
  },

  asyncComputed: {
    canUpdatePerson(){
      return this.canIDo('person', 'update');
    },
  },

  created() {
    const countries = require('i18n-iso-countries')
    countries.registerLocale(require('i18n-iso-countries/langs/sv.json'))
    const countryNames = countries.getNames('sv')
    for (const [key, value] of Object.entries(countryNames)) {
      this.countries.push({key: key, value: value})
    }
    this.countries = orderBy(this.countries, ['value'], ['asc'])
  },

  mounted() {
    this.editing = !this.isNew
  },

  watch: {
    editing() {
      if (this.editing) {
        this.savedCopy = pick(this.selectedPerson, this.fields)
      } else {
        this.fields.map(f => {
          this.selectedPerson[f] = this.savedCopy[f]
        })
      }
    }
  },

  methods: {
    ...mapActions('person', ['updateSelectedPerson', 'setSelectedPerson']),

    // @vuese
    //- Update or Create person based on person id if it exist or not
    async saveContactInfo() {
      if (!this.$refs.form.validate()) {
        this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
        this.selectedPerson.id = -1
        return
      }

      //- If person added is a minor, show popup to confirm 
      if ( this.rules.isMinor(this.selectedPerson.personalNumber) ) {
        const res = await this.$dialog.confirm({
          title: 'OBS!',
          text: 'Personen du försöker lägga till är under 18 år – ett godkänt undantag krävs.',
          width: '700',
          actions: [
            {text: 'Avbryt', key: false, icon: '$sl-cancel'},
            {text: 'Jag har förstått detta', key: true,  icon: '$sl-check', outlined: true}
          ]
        })

        if (res !== true) return
      }
      
      let updatedFields = {}
      if (this.$route.params.id) {
        updatedFields.id = this.$route.params.id
      }

      this.fields.map(f => {
        updatedFields[f] = this.selectedPerson[f]
      })
      if (this.selectedPerson && this.selectedPerson.zip == '') {
        delete updatedFields.zip
      }
      if (!updatedFields.email) {
        updatedFields.email = null
      }
      if (this.selectedPerson && this.selectedPerson.country && this.selectedPerson.country != '') {
        updatedFields.country = this.selectedPerson.country
      } else {
        updatedFields.country = 'SE'
      }

      API.setPerson(updatedFields)
        .then(result => {
          if (!this.$route.params.id) {
            this.$nextTick(() => {
              this.$router.replace({name: 'PersonDetail', params: {id: result.data.id}})
            })
            return
          }
          // Used to update local store with new updated person fields
          // @arg The argument is an object representing person
          this.updateSelectedPerson(updatedFields)
          this.savedCopy = pick(this.selectedPerson, this.fields)
          this.editing = false
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
          )
        })
    }
  }
}
</script>
